import React, { useEffect, useState } from "react";
import { Container, Row, Col, Card, Button, Form, Spinner } from "react-bootstrap";
import { QRCodeSVG } from "qrcode.react";
import { Utils } from "../../common/Utils";
import { SiteSettings, siteSettingsDefault } from "../../model/SiteSettingModel";
import { SettingGateway } from "../../gateways/SettingGateway";
import { deleteObject, getDownloadURL, ref, uploadBytesResumable } from "firebase/storage";
import { storage } from "../../firebaseConfig";

const printStyles = `
@media print {
  .print-container {
  }

  .container h2{
    display:none;
  }

  .instructions{
    display:none;
    }

  .App-footer{
    display:none;
  }

  .container .controlRow{
    display:none;
  }

  .print-container .card {
    page-break-inside: avoid; /* Prevents card splitting */
    break-inside: avoid; /* Additional support for modern browsers */
  }

  .print-container .row {
    display: flex;
    flex-wrap: wrap;
  }

  .my-3
  {
    margin-top: 0px !important;
    margin-bottom: 0px !important;
  }

  .card-text
  {
    margin-bottom: 0px !important;
  }

  .navbar {
    display: none;
  }
}
`;

interface Label {
    id: number,
    location: string | null,
    url: string
}

const DeliveryLocationGenerator: React.FC = () => {
    const [prefix, setPrefix] = useState("");
    const [locationCount, setLocationCount] = useState<number | "">("");
    const [locations, setLocations] = useState("");
    const [labelData, setLabelData] = useState<Label[]>([]);
    const [settings, setSettings] = useState<SiteSettings>(siteSettingsDefault);
    const [cardSize, setCardSize] = useState<'small' | 'medium' | 'large'>('medium');

    useEffect(() => {
        const fetchSettings = async () => {
            var settings = await SettingGateway.GetSettings();
            if (settings) {
                setSettings(settings);
            }
        };

        fetchSettings();
    }, []);

    const handleGenerate = () => {

        var alias = Utils.GetAlias();
        var labels: Label[] = [];
        if (prefix && locationCount) {
            labels = Array.from({ length: Number(locationCount) }, (_, i) => ({ id: i, location: `${prefix}${i + 1}`, url: `${window.location.origin}/${alias ? `${alias}/` : ""}d/${`${prefix}${i + 1}`}` }));
        }
        if (locations) {
            labels = labels.concat(locations.split(/[,;\n]+/).map((loc, i) => ({ id: i + labels.length, location: loc, url: `${window.location.origin}/${alias ? `${alias}/` : ""}d/${loc}` })));
        }
        if (labels.length === 0) {
            labels.push({ id: 1, location: null, url: `${window.location.origin}/${alias ? `${alias}` : ""}` });
        }

        setLabelData(labels);
    };

    const handlePrint = () => {
        window.print();
    };

    // Image upload state
    const [image, setImage] = useState<File | null>(null);
    const [uploading, setUploading] = useState<boolean>(false);

    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            setImage(e.target.files[0]);
        }
    };

    const deleteImage = async () => {
        if (!settings.siteLabelLogo) return;
        try {
            setUploading(true);
            const imageRef = ref(storage, settings.siteLabelLogo);
            await deleteObject(imageRef);
            setSettings((prev) => ({ ...prev, siteLabelLogo: '' }));
        } catch {
            alert('Error deleting image.');
        } finally {
            setUploading(false);
        }
    };

    const uploadImageAndSaveLogo = async () => {
        setUploading(true);
        if (!image) return;
        const storageRef = ref(storage, `siteLogo/${image.name}`);
        const uploadTask = uploadBytesResumable(storageRef, image);
        uploadTask.on(
            'state_changed',
            null,
            (error) => {
                setUploading(false);
                alert('Error uploading image.');
                console.error("Error saving the image", error);
            },
            async () => {
                const imageUrl = await getDownloadURL(uploadTask.snapshot.ref);
                setSettings((prev) => ({ ...prev, siteLabelLogo: imageUrl }));
                setImage(null);
                setUploading(false);
            }
        );
    };

    // Card size styles
    const cardSizeStyles = {
        small: { card: { minWidth: '180px', maxWidth: '220px', fontSize: '0.85rem' }, qr: 80, logo: 28, md: 4 },
        medium: { card: { minWidth: '300px', maxWidth: '400px', fontSize: '1rem' }, qr: 252, logo: 84, md: 6 },
        large: { card: { minWidth: '640px', maxWidth: '800px', fontSize: '1.2rem' }, qr: 380, logo: 120, md: 12 },
    };

    return (
        <>
            <style>{printStyles}</style>
            <Container className="main-container mt-4">
                <h2>Delivery Location Generator</h2>
                <div className="instructions">
                    <p>This page allows you to generate QR codes for multiple delivery locations. It is ideal for offices or hospitals where you want users to easily scan the QR code and place an order with their delivery location already set. This is especially useful in environments with multiple desks, offices, or hospital beds.</p>
                    <p>The page allows you to select a <strong>Delivery Location Prefix</strong> (for example, Desk) and the <strong>Number of locations</strong> (for example, 20). This will create QR codes with delivery locations set to Desk1, Desk2, ..., Desk20.</p>
                    <p>You can also enter a list of custom locations (for example, Foyer, Kitchen, Reception).</p>
                </div>
                <Row className="controlRow">
                    <Col md={4}>
                        <Form.Group controlId="cardSize">
                            <Form.Label>Card Size</Form.Label>
                            <Form.Select value={cardSize} onChange={e => setCardSize(e.target.value as 'small' | 'medium' | 'large')}>
                                <option value="small">Small</option>
                                <option value="medium">Medium</option>
                                <option value="large">Large</option>
                            </Form.Select>
                        </Form.Group>
                    </Col>
                </Row>
                <Row className="controlRow">
                    <Col md={6}>
                        <Form.Group controlId="deliveryPrefix">
                            <Form.Label>Delivery Location Prefix</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Enter prefix (e.g., Store-)"
                                value={prefix}
                                onChange={(e) => setPrefix(e.target.value)}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group controlId="locationCount">
                            <Form.Label>Number of Locations</Form.Label>
                            <Form.Control
                                type="number"
                                placeholder="Enter number of locations"
                                value={locationCount}
                                onChange={(e) => setLocationCount(Number(e.target.value))}
                                min={1}
                            />
                        </Form.Group>
                    </Col>
                </Row>
                <Row className="mt-3 controlRow">
                    <Col>
                        <Form.Group controlId="deliveryLocations">
                            <Form.Label>All Delivery Locations</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={5}
                                placeholder="Enter locations separated by ',' ';' or 'Enter'"
                                value={locations}
                                onChange={(e) => setLocations(e.target.value)}
                            />
                            <small className="text-muted">Separate locations using ',' ';' or pressing 'Enter'.</small>
                        </Form.Group>
                    </Col>
                </Row>
                <Row className="mt-3 controlRow">
                    <Col>
                        <Form.Group className="mb-3">
                            <Form.Label>Site Label Image</Form.Label>
                            {!settings.siteLabelLogo && (
                                <>
                                    <Form.Control type="file" accept="image/*" onChange={handleImageChange} disabled={uploading} />
                                    <Button variant="secondary" className="mt-2" onClick={uploadImageAndSaveLogo} disabled={!image || uploading}>
                                        {uploading ? <Spinner animation="border" size="sm" /> : "Upload Image"}
                                    </Button>
                                </>
                            )}
                            {(settings.siteLabelLogo || settings.siteLogo) && (
                                <div className='mt-2 mb-2'>
                                    <img src={settings.siteLabelLogo && settings.siteLabelLogo !== '' ? settings.siteLabelLogo : settings.siteLogo} alt="Site Logo" style={{ maxHeight: 40, objectFit: "cover", borderRadius: 8, border: "1px solid #ccc" }} />
                                    {settings.siteLabelLogo && <Button variant="danger" className="ms-2" onClick={deleteImage} disabled={uploading}>
                                        {uploading ? <Spinner animation="border" size="sm" /> : "Delete Image"}
                                    </Button>}
                                </div>
                            )}
                        </Form.Group>
                    </Col>
                </Row>
                <Row className="mt-3 controlRow">
                    <Col className="d-flex">
                        <Button variant="primary" className="me-2" onClick={handleGenerate}>
                            Generate
                        </Button>
                        <Button variant="secondary" onClick={handlePrint}>
                            Print labels
                        </Button>
                    </Col>
                </Row>

                <Row className="my-4 print-container">
                    <Row className="mt-3">
                        {labelData.map((card) => (
                            <Col md={cardSizeStyles[cardSize].md} key={card.id} className="mb-4 d-flex">
                                <Card className="text-center flex-fill p-3" style={cardSizeStyles[cardSize].card}>
                                    <Card.Img
                                        variant="top"
                                        src={settings ? (settings.siteLabelLogo && settings.siteLabelLogo !== '' ? settings.siteLabelLogo : settings.siteLogo) : "OrderQ"}
                                        className="mx-auto d-block"
                                        style={{ maxWidth: "80%", maxHeight: cardSizeStyles[cardSize].logo + 'px', width: "auto", height: "auto" }}
                                    />

                                    <Card.Body className="d-flex flex-column justify-content-between">
                                        {card.location && <div>
                                            <Card.Text>Deliver to your location:</Card.Text>
                                            <Card.Title style={{ fontSize: cardSize === 'small' ? '1.1em' : cardSize === 'medium' ? '1.5em' : '2.5em' }}>{card.location}</Card.Title>
                                        </div>}
                                        {!card.location && <Card.Title style={{ fontSize: cardSize === 'small' ? '1.1em' : cardSize === 'medium' ? '1.5em' : '2.5em' }}>Order Here</Card.Title>}
                                        <div>
                                            <QRCodeSVG value={card.url} size={cardSizeStyles[cardSize].qr} className="my-3 qr-code" style={{padding: cardSize === 'small' ?'0px': cardSize === 'medium' ? '10px' : '20px'}} />
                                            <Card.Text className="mt-2">
                                                <a href={card.url} target="_blank" rel="noopener noreferrer">
                                                    {card.url}
                                                </a>
                                            </Card.Text>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                        ))}
                    </Row>
                </Row>
            </Container>
        </>
    );
};

export default DeliveryLocationGenerator;